import React, { useEffect, useRef, useState } from 'react';
import { ButtonV2 } from '../../../atoms/v2/button/button-v2.atom';
import { SectionTitleV2 } from '../../../atoms/v2/section-title/section-title.atom';
import { CheckoutForm } from '../../../organisms/classic/checkout-form/checkout-form.organism';
import { OrderBump } from '../../../organisms/order-bump/order-bump.organism';
import { PaymentMethods } from '../../../organisms/classic/payment-methods/payment-methods.organism';
import { OrderSummaryClassic } from '../../../molecules/classic-checkout/order-summary/order-summary-classic/order-summary-classic.molecule';
import * as S from '../checkout.style';
import { MetodoPagamentoVendaEnum } from '../../../../common/types/enums/metodo-pagamento.enum';
import {
  Checkout,
  DraggableComponents,
} from '../../../../common/types/checkout';
import { CreatePurchaseContract } from '../../../../common/types/create-purchase-contract';
import { useCheckoutController } from '../../../../common/hooks/useCheckoutController/useCheckoutController';
import { Trans, useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import { EventState } from '../../../../common/redux/slices/event.slice';
import { useSelector } from 'react-redux';
import {
  CountryData,
  useCountriesWithPhoneMasks,
} from '../../../../common/hooks/useCountries';
import { MpCardSectionRef } from '../../../molecules/mp-card-section/mp-card-section';
import { LoadingScreen } from '../../../molecules/loading-screen/loading-screen.molecule';
import { DeliveryForm } from '../../../organisms/classic/delivery-form/delivery-form.organism';
import { StripeElementRef } from '../../../atoms/stripe-inputs/stripe-card-input/stripe-card-input';
import { StripeState } from '../../../../common/hooks/useCheckoutController/useCheckoutController.types';
import { CouponSection } from '../../../molecules/coupon-section/coupon-section.molecule';
import { CardHeaderPhoto } from '../../../molecules/classic-checkout/card-header/card-header-photo/card-header-photo';
import { CardHeaderClassic } from '../../../molecules/classic-checkout/card-header/card-header-classic/card-header-classic';
import { OrderSummaryCut } from '../../../molecules/classic-checkout/order-summary/order-summary-cut/order-summary-cut.molecule';
import { OrderSummaryHole } from '../../../molecules/classic-checkout/order-summary/order-summary-hole/order-summary-hole.molecule';
import { OrderSummaryTorn } from '../../../molecules/classic-checkout/order-summary/order-summary-torn/order-summary-torn';
import { OrderSummaryCard } from '../../../molecules/classic-checkout/order-summary/order-summary-card/order-summary-card.molecule';
import { Timer } from '../../../molecules/classic-checkout/timer/timer.molecule';
import { TextPreview } from '../../../atoms/classic-checkout/text/text.atom';
import { ClassicCheckoutImage } from '../../../atoms/classic-checkout/image/image.atom';
import { ClassicCheckoutDivider } from '../../../atoms/classic-checkout/divider/divider.atom';
import { CustomHtmlAtom } from '../../../atoms/classic-checkout/custom-html/custom-html.atom';
import { GuaranteeBadge } from '../../../molecules/classic-checkout/guarantee-badge/guarantee-badge.molecule';
import { SocialProofPreview } from '../../../molecules/classic-checkout/social-proof/social-proof.molecule';
import { ExpressCheckoutCard } from '../../../molecules/express-card/express-card-classic/express-card-classic.molecule';
import Logo from '../../../../assets/logo-full.png';
import Google from '../../../../assets/google.png';
import Recatpcha from '../../../../assets/recaptcha.png';
import SSL from '../../.././../common/assets/ssl-secure.png';
import SecurePayment from '../../.././../common/assets/secure-payment.png';
import { CheckoutFormSkeleton } from '../../../molecules/checkout-form-skeleton/checkout-form-skeleton.molecule';
import { PaymentMethodsSkeleton } from '../../../molecules/payment-method-skeleton/payment-method-skeleton.molecule';
import { OrderSummaryClassicSkeleton } from '../../../molecules/order-summary-skeleton/order-summary-skeleton.molecule';
import { OfferCardSkeleton } from '../../../molecules/checkout-form-skeleton/checkout-form-skeleton.molecule.style';

type Props = {
  checkout: Checkout;
  paymentMethod: MetodoPagamentoVendaEnum;
  setPaymentMethod: (pm: MetodoPagamentoVendaEnum) => void;
  onSubmit: (data: CreatePurchaseContract) => void;
  onToggleOrderBump: (applied: boolean, id: string) => void;
  isLoading: boolean;
  onApplyCoupon: (coupon: string) => void;
  couponMessage: string;
  discount: number;
  applePay?: {
    available: boolean;
    onClick: () => void;
    loading: boolean;
  };
  googlePay?: {
    available: boolean;
    onClick: () => void;
    loading: boolean;
  };
  amazonPay?: {
    available: boolean;
    onClick: () => void;
    loading: boolean;
  };
  paypal?: {
    available: boolean;
    loading: boolean;
    currency: string;
    createOrder?: () => Promise<string>;
    createSubscription?: () => Promise<string>;
    onApprove: (data: { orderID: string; subscriptionID?: string }) => Promise<void>;
    onCancel: () => void;
    isSubscription?: boolean;
  };
};

export const OnePageCheckout: React.FC<Props> = ({
  checkout,
  paymentMethod,
  setPaymentMethod,
  onSubmit,
  onToggleOrderBump,
  isLoading,
  onApplyCoupon,
  couponMessage,
  discount,
  applePay,
  googlePay,
  amazonPay,
  paypal,
}) => {
  const { t } = useTranslation();
  const alert = useAlert();
  const eventId = useSelector((s: { event: EventState }) => s.event.eventId);
  const mpCardRef = useRef<MpCardSectionRef>(null);
  const [selectedCountry, setSelectedCountry] = useState<
    CountryData | undefined
  >(undefined);
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [desiredBumps, setDesiredBumps] = React.useState<
    Record<string, boolean>
  >({});
  const [baseSubtotal, setBaseSubtotal] = React.useState<number>(0);
  const [stripeState, setStripeState] = React.useState<StripeState>({
    number: { complete: false, error: null },
    expiry: { complete: false, error: null },
    cvc: { complete: false, error: null },
  });

  const baselineLockedRef = React.useRef(false);
  const lastCheckoutIdRef = React.useRef<string | undefined>(undefined);
  const numberRef = useRef<StripeElementRef>(null);
  const expiryRef = useRef<StripeElementRef>(null);
  const cvcRef = useRef<StripeElementRef>(null);

  const { formData, handleChange, handlePurchaseClick } = useCheckoutController(
    {
      checkout,
      paymentMethod,
      onSubmit,
      t,
      alert,
      eventId,
      mpCardRef,
      stripeState,
      stripeNumberRef: numberRef,
      stripeExpiryRef: expiryRef,
      stripeCvcRef: cvcRef,
    },
  );

  const handleStripeChange = (
    field: 'number' | 'expiry' | 'cvc',
    s: { complete: boolean; error: string | null },
  ) => {
    setStripeState((prev) => ({ ...prev, [field]: s }));
  };

  const countries = useCountriesWithPhoneMasks();

  const filteredCountries = countries.filter(
    (country) =>
      country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
      country.dialCode.includes(countrySearch),
  );

  const handleOrderBumpToggle = (checked: boolean, id: string) => {
    setDesiredBumps((prev) => ({ ...prev, [id]: checked }));
    onToggleOrderBump(checked, id);
  };

  const { uiOrderBumpsAplicados, uiSubtotal } = React.useMemo(() => {
    const appliedList =
      (checkout?.orderBumps ?? []).filter((ob) => desiredBumps[ob.id]) ?? [];

    const bumpsSum = appliedList.reduce((acc, ob) => {
      const price = ob?.precos?.[0]?.valor ?? 0;
      return acc + price;
    }, 0);

    const subtotal = (baseSubtotal ?? 0) + bumpsSum;

    return {
      uiOrderBumpsAplicados: appliedList.map((ob) => ({
        id: ob.id,
        produtoOferta: ob.produtoOferta,
        precos: ob.precos,
      })),
      uiSubtotal: subtotal,
    };
  }, [checkout?.orderBumps, desiredBumps, baseSubtotal]);

  const renderAditionalComponent = (component: DraggableComponents) => {
    switch (component.type) {
      case 'timer':
        return <Timer component={component} />;
      case 'text':
      case 'text':
        const links = (component.style?.links ?? []) as Array<{
          id: string;
          start: number;
          end: number;
          href: string;
          newTab?: boolean;
        }>;

        const text = component.content ?? '';

        const safeLinks = [...links]
          .filter(
            (l) =>
              l &&
              l.start >= 0 &&
              l.end > l.start &&
              l.end <= text.length &&
              l.href?.trim(),
          )
          .sort((a, b) => a.start - b.start);

        const nodes: React.ReactNode[] = [];
        let cursor = 0;

        safeLinks.forEach((l) => {
          if (l.start > cursor) nodes.push(text.slice(cursor, l.start));

          const chunk = text.slice(l.start, l.end);
          nodes.push(
            <a
              key={l.id}
              href={l.href}
              target={l.newTab ? '_blank' : undefined}
              rel={l.newTab ? 'noopener noreferrer' : undefined}
              style={{
                textDecoration: 'underline',
                color: component.style.color,
              }}
            >
              {chunk}
            </a>,
          );

          cursor = l.end;
        });

        if (cursor < text.length) nodes.push(text.slice(cursor));

        return (
          <TextPreview style={component.style}>
            {nodes}
          </TextPreview>
        );
      case 'image':
        return <ClassicCheckoutImage component={component} />;
      case 'divider':
        return <ClassicCheckoutDivider />;
      case 'advantages':
        return <GuaranteeBadge component={component} />;
      case 'social-proof':
        return <SocialProofPreview component={component} />;
      case 'custom-html':
        return <CustomHtmlAtom component={component} />;
    }
  };

  useEffect(() => {
    if (!checkout?.id) return;
    const map: Record<string, boolean> = {};
    const appliedSet = new Set(checkout?.orderBumpsAplicados?.map((o) => o.id));
    checkout?.orderBumps?.forEach((ob) => {
      map[ob.id] = appliedSet.has(ob.id);
    });
    setDesiredBumps(map);
  }, [checkout?.id]);

  useEffect(() => {
    setSelectedCountry(countries?.find((c) => c.iso2 === checkout?.pais));
  }, [countries, checkout?.pais]);

  useEffect(() => {
    if (lastCheckoutIdRef.current !== checkout?.id) {
      baselineLockedRef.current = false;
      lastCheckoutIdRef.current = checkout?.id;
    }

    if (!checkout?.id || baselineLockedRef.current) return;
    const appliedSet = new Set(checkout?.orderBumpsAplicados?.map((o) => o.id));
    const initialDesired: Record<string, boolean> = {};
    (checkout?.orderBumps ?? []).forEach((ob) => {
      initialDesired[ob.id] = appliedSet.has(ob.id);
    });
    setDesiredBumps(initialDesired);
    const serverAppliedSum = (checkout?.orderBumpsAplicados ?? []).reduce(
      (acc, ob) => {
        const price = ob?.precos?.[0]?.valor ?? 0;
        return acc + price;
      },
      0,
    );
    setBaseSubtotal((checkout?.subtotal ?? 0) - serverAppliedSum);
    baselineLockedRef.current = true;
  }, [
    checkout?.id,
    checkout?.subtotal,
    checkout?.orderBumpsAplicados,
    checkout?.orderBumps,
  ]);


  return (
    <S.Container>
      <S.ContentWrapper>
        {checkout?.checkoutConfig?.config?.componentsAbove?.map((c) =>
          renderAditionalComponent(c),
        )}
        <S.Card>
          {!isLoading && (
            <>
              {checkout?.checkoutConfig?.config?.checkoutData?.checkoutHeader
                ?.variant === 'photo' && (
                <CardHeaderPhoto checkout={checkout} />
              )}

              {(checkout?.checkoutConfig?.config?.checkoutData?.checkoutHeader
                ?.variant === 'classic' ||
                !checkout?.checkoutConfig?.config?.checkoutData?.checkoutHeader
                  ?.variant) && <CardHeaderClassic checkout={checkout} />}
            </>
          )}
          {((checkout?.metodosPagamento?.includes('applepay') &&
            applePay?.available) ||
            (checkout?.metodosPagamento?.includes('googlepay') &&
              googlePay?.available) ||
            checkout?.metodosPagamento?.includes('amazonpay') ||
            paypal?.available) && (
            <ExpressCheckoutCard
              applePay={applePay}
              googlePay={googlePay}
              amazonPay={amazonPay}
              paypal={paypal}
            />
          )}

          {isLoading ? (
            <CheckoutFormSkeleton />
          ) : (
            <CheckoutForm
              formData={formData}
              setFormData={handleChange}
              selectedCountry={selectedCountry ?? ({} as CountryData)}
              setSelectedCountry={setSelectedCountry}
              showCountryPicker={showCountryPicker}
              setShowCountryPicker={setShowCountryPicker}
              countrySearch={countrySearch}
              setCountrySearch={setCountrySearch}
              checkout={checkout}
              filteredCountries={filteredCountries}
            />
          )}

          {checkout?.exibirEndereco && (
            <DeliveryForm
              checkout={checkout}
              formData={formData}
              setFormData={handleChange}
            />
          )}

          {isLoading ? (
            <PaymentMethodsSkeleton />
          ) : (
            <PaymentMethods
              handleStripeChange={handleStripeChange}
              cvcRef={cvcRef}
              numberRef={numberRef}
              expiryRef={expiryRef}
              mpCardRef={mpCardRef}
              formData={formData}
              selectedMethod={paymentMethod}
              setSelectedMethod={setPaymentMethod}
              checkout={checkout}
              onFormDataChange={handleChange}
              applePay={applePay}
              googlePay={googlePay}
              handlePlaceOrder={handlePurchaseClick}
            />
          )}

          {checkout?.orderBumps?.length > 0 && false && (
            <SectionTitleV2 style={{ marginTop: '24px' }}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                  d="m9 14.25 6-6m4.5-3.493V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0c1.1.128 1.907 1.077 1.907 2.185ZM9.75 9h.008v.008H9.75V9Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm4.125 4.5h.008v.008h-.008V13.5Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
                />
              </svg>
              <span>{t('limitedOffer')}</span>
            </SectionTitleV2>
          )}

          {checkout?.orderBumps?.map((o) => (
            <OrderBump
              key={o.id}
              orderBump={o}
              isChecked={
                !!checkout?.orderBumpsAplicados?.find((_o) => _o.id === o.id)
              }
              onToggle={handleOrderBumpToggle}
              checkout={checkout}
            />
          ))}
          {isLoading ? (
            <OrderSummaryClassicSkeleton />
          ) : checkout?.checkoutConfig?.config?.checkoutData?.orderSummary
              ?.variant === 'classic' ||
            !checkout?.checkoutConfig?.config?.checkoutData?.orderSummary
              ?.variant ? (
            <OrderSummaryClassic
              discount={discount}
              checkout={checkout}
              uiOrderBumpsAplicados={uiOrderBumpsAplicados}
              uiSubtotal={uiSubtotal}
            />
          ) : null}

          {checkout?.checkoutConfig?.config?.checkoutData?.orderSummary
            ?.variant === 'cut' && (
            <OrderSummaryCut
              discount={discount}
              checkout={checkout}
              uiOrderBumpsAplicados={uiOrderBumpsAplicados}
              uiSubtotal={uiSubtotal}
            />
          )}

          {checkout?.checkoutConfig?.config?.checkoutData?.orderSummary
            ?.variant === 'hole' && (
            <OrderSummaryHole
              discount={discount}
              checkout={checkout}
              uiOrderBumpsAplicados={uiOrderBumpsAplicados}
              uiSubtotal={uiSubtotal}
            />
          )}

          {checkout?.checkoutConfig?.config?.checkoutData?.orderSummary
            ?.variant === 'torn' && (
            <OrderSummaryTorn
              discount={discount}
              checkout={checkout}
              uiOrderBumpsAplicados={uiOrderBumpsAplicados}
              uiSubtotal={uiSubtotal}
            />
          )}

          {checkout?.checkoutConfig?.config?.checkoutData?.orderSummary
            ?.variant === 'card' && (
            <OrderSummaryCard
              discount={discount}
              checkout={checkout}
              uiOrderBumpsAplicados={uiOrderBumpsAplicados}
              uiSubtotal={uiSubtotal}
            />
          )}

          {checkout?.exibirCupomDesconto && (
            <CouponSection
              checkout={checkout}
              couponMessage={couponMessage}
              onApplyCoupon={onApplyCoupon}
              discount={discount}
            />
          )}

          {isLoading ? (
            <OfferCardSkeleton className='mt-4'/>
          ) : (
            <S.SubmitButton>
              <ButtonV2
                background={
                  checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
                    ?.background
                }
                color={
                  checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
                    ?.color
                }
                onClick={() => handlePurchaseClick()}
                variant="primary"
              >
                {t('purchase')}
              </ButtonV2>
            </S.SubmitButton>
          )}

          {!isLoading && (
            <S.Footer>
              <S.VendepayLogo>
                {t('protectedBy')} &nbsp;
                <S.Logo src={Logo} alt="Logo da vendepay" />
              </S.VendepayLogo>
            </S.Footer>
          )}
        </S.Card>
        {checkout?.checkoutConfig?.config?.componentsBelow?.map((component) =>
          renderAditionalComponent(component),
        )}
        <S.PrincipalFooter>
          {/*<S.SmallText>{t('recaptchaNotice')}</S.SmallText>*/}
          <S.SmallText>
            <strong style={{ color: '#a0a0a0' }}>
              {t('siteProtectedByPrefix')}&nbsp;
            </strong>
            <S.Recaptcha src={Recatpcha} alt="reCAPTCHA" />
            <strong style={{ color: '#a0a0a0' }}>
              &nbsp;{t('siteProtectedByConnector')}&nbsp;
            </strong>
            <S.Google src={Google} alt="Google" />
            <strong
              style={{
                color: '#a0a0a0',
                display: 'block',
                marginTop: '4px',
              }}
            >
              {t('privacyPolicy')} &nbsp;
              {t('and')} {t('termsOfService')}
            </strong>
            <div style={{ opacity: '0.6' }}>
              <img
                style={{ width: '99px', height: '99px', marginTop: '-20px' }}
                //className="safe"
                src={SSL}
              />
              <img
                style={{ width: '69px', height: '69px', marginTop: '-20px' }}
                //className="safe"
                src={SecurePayment}
              />
            </div>
          </S.SmallText>
        </S.PrincipalFooter>
      </S.ContentWrapper>
    </S.Container>
  );
};
